import React, { useState, useEffect } from 'react';
import { CheckCircle2, Loader2, PlugZap, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSettingsApi } from '../../../common/hooks/use-settings-api.hook';
import { useEmailApi } from '../../../common/hooks/use-email-api.hook';
import { SmtpConfig } from '../../../modules/vendor/settings/models/config.model';
import { useAutosave } from '../../../common/hooks/use-autosave.hook';

const EMPTY_SMTP: SmtpConfig = {
  host: '',
  port: 587,
  user: '',
  pass: '',
  from: '',
  fromName: '',
  marketingPostalAddress: '',
};

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Label className="mb-1.5 block text-xs text-muted-foreground">{children}</Label>
);

/** The success/failure line under the form; the page shows two kinds. */
const ResultNote = ({ success, children }: { success: boolean; children: React.ReactNode }) => (
  <div
    role="alert"
    className={cn(
      'mt-5 flex items-center gap-2 border px-3 py-2 text-xs',
      success ? 'border-success/30 bg-success/10 text-success' : 'border-destructive/30 bg-destructive/10 text-destructive',
    )}
  >
    {success ? <CheckCircle2 className="size-4 shrink-0" /> : <XCircle className="size-4 shrink-0" />}
    {children}
  </div>
);

export const EmailSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const settingsApi = useSettingsApi();
  const emailApi = useEmailApi();

  const [smtp, setSmtp] = useState<SmtpConfig>(EMPTY_SMTP);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [hasExistingPassword, setHasExistingPassword] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const config = await settingsApi.findConfig();
      if (config?.smtp) {
        setSmtp(config.smtp);
        setHasExistingPassword(config.smtp.pass === '••••••••');
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof SmtpConfig) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = field === 'port' ? parseInt(e.target.value) || 0 : e.target.value;
    setSmtp(prev => ({ ...prev, [field]: value }));
    setTestResult(null);
    if (field === 'pass' && hasExistingPassword) {
      setHasExistingPassword(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await emailApi.testSmtpConnection({
        host: smtp.host,
        port: smtp.port,
        user: smtp.user,
        pass: hasExistingPassword && !smtp.pass ? '••••••••' : smtp.pass,
      });
      setTestResult({
        success: result.connected,
        message: result.connected ? t('EMAIL_SETTINGS.TEST_SUCCESS') : t('EMAIL_SETTINGS.TEST_FAILED'),
      });
    } catch (error) {
      setTestResult({ success: false, message: t('EMAIL_SETTINGS.TEST_FAILED') });
    } finally {
      setTesting(false);
    }
  };

  const isFormValid = smtp.host && smtp.port && smtp.user && (smtp.pass || hasExistingPassword) && smtp.from;

  /*
   * Saved a moment after the last change, and only while the form is complete:
   * a server half typed in is not saved over one that works. The password
   * stays as typed after a save — swapping it for the mask, as the old Save
   * did, would scramble one still being typed.
   */
  useAutosave({
    data: smtp,
    ready: !loading && !!isFormValid,
    save: () => settingsApi.updateConfig({ smtp }),
  });

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl">
      <div className="mb-6">
        <h1 className="font-heading mb-1 text-xl font-medium tracking-tight">{t('EMAIL_SETTINGS.TITLE')}</h1>
        <p className="text-sm text-muted-foreground">{t('EMAIL_SETTINGS.DESCRIPTION')}</p>
      </div>

      <Card>
        <CardContent>
          <p className="mb-5 text-lg font-medium">{t('EMAIL_SETTINGS.SMTP_CONFIG')}</p>

          <div className="flex flex-col gap-4">
            {/* Host + Port */}
            <div className="flex gap-4">
              <div className="flex-1">
                <FieldLabel>{t('EMAIL_SETTINGS.HOST')}</FieldLabel>
                <Input placeholder="smtp.gmail.com" value={smtp.host} onChange={handleChange('host')} />
              </div>
              <div className="w-30 shrink-0">
                <FieldLabel>{t('EMAIL_SETTINGS.PORT')}</FieldLabel>
                <Input placeholder="587" type="number" value={smtp.port || ''} onChange={handleChange('port')} />
              </div>
            </div>

            {/* User + Password */}
            <div className="flex gap-4">
              <div className="flex-1">
                <FieldLabel>{t('EMAIL_SETTINGS.USERNAME')}</FieldLabel>
                <Input placeholder="user@example.com" value={smtp.user} onChange={handleChange('user')} />
              </div>
              <div className="flex-1">
                <FieldLabel>{t('EMAIL_SETTINGS.PASSWORD')}</FieldLabel>
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={hasExistingPassword ? '••••••••••••' : smtp.pass}
                  onChange={handleChange('pass')}
                  onFocus={() => {
                    if (hasExistingPassword) {
                      setHasExistingPassword(false);
                      setSmtp(prev => ({ ...prev, pass: '' }));
                    }
                  }}
                />
              </div>
            </div>

            {/* From email + From name */}
            <div className="flex gap-4">
              <div className="flex-1">
                <FieldLabel>{t('EMAIL_SETTINGS.FROM_EMAIL')}</FieldLabel>
                <Input placeholder="noreply@yourdomain.com" value={smtp.from} onChange={handleChange('from')} />
              </div>
              <div className="flex-1">
                <FieldLabel>{t('EMAIL_SETTINGS.FROM_NAME')}</FieldLabel>
                <Input placeholder="My Company" value={smtp.fromName} onChange={handleChange('fromName')} />
              </div>
            </div>

            {/* Postal address — required in the footer of every marketing email
                (CAN-SPAM). Automations cannot be activated until this is set. */}
            <div>
              <FieldLabel>{t('EMAIL_SETTINGS.POSTAL_ADDRESS')}</FieldLabel>
              <Textarea
                placeholder="123 Main Street, Suite 100, New York, NY 10001, USA"
                rows={2}
                value={smtp.marketingPostalAddress || ''}
                onChange={handleChange('marketingPostalAddress')}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">{t('EMAIL_SETTINGS.POSTAL_ADDRESS_HELP')}</p>
            </div>
          </div>

          {testResult && <ResultNote success={testResult.success}>{testResult.message}</ResultNote>}

          <div className="mt-6 flex justify-end gap-3">
            <Button variant="outline" onClick={handleTestConnection} disabled={testing || !isFormValid}>
              {testing ? <Loader2 className="size-3.5 animate-spin" /> : <PlugZap className="size-4" />}
              {t('EMAIL_SETTINGS.TEST_CONNECTION')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailSettingsPage;
